import React, { Suspense, lazy, useEffect, useRef, useState } from "react";

/* Reusable immersive background.
   - On desktop (>=768px, motion allowed): lazy-loads the Three.js scene.
   - On mobile / reduced-motion: CSS gradient fallback, no heavy 3D chunk.
   - Pauses the render loop when scrolled off-screen or the tab is hidden,
     so only the currently visible canvas ever consumes the GPU.
   variant: "particles" | "constellation" | "orbs" */

const ThreeScene = lazy(() => import("./three/ThreeScene"));

export default function ThreeBackground({ variant = "particles", color = "cyan", className = "" }) {
  const ref = useRef(null);
  const [enabled, setEnabled] = useState(false);
  const [inView, setInView] = useState(true);
  const [tabVisible, setTabVisible] = useState(true);

  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const wide = window.matchMedia("(min-width: 768px)");
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setEnabled(wide.matches && !reduce.matches);
    update();
    wide.addEventListener("change", update);
    reduce.addEventListener("change", update);
    return () => {
      wide.removeEventListener("change", update);
      reduce.removeEventListener("change", update);
    };
  }, []);

  // Pause when the canvas scrolls out of view.
  useEffect(() => {
    if (!enabled || !ref.current) return undefined;
    const io = new IntersectionObserver(
      ([e]) => setInView(e.isIntersecting),
      { threshold: 0, rootMargin: "120px" }
    );
    io.observe(ref.current);
    return () => io.disconnect();
  }, [enabled]);

  // Pause when the browser tab is hidden.
  useEffect(() => {
    const onVis = () => setTabVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, []);

  return (
    <div ref={ref} className={`cv-three cv-three--${variant} ${className}`} aria-hidden="true">
      {enabled ? (
        <Suspense fallback={<div className="cv-three__fallback" />}>
          <ThreeScene variant={variant} color={color} paused={!inView || !tabVisible} />
        </Suspense>
      ) : (
        <div className="cv-three__fallback" />
      )}
    </div>
  );
}
